import React, { useMemo } from 'react';
import { Card, Row, Col, Statistic, Descriptions, Space, Typography, Divider, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CalculatorData } from './Calculator';
import { MIN_PURCHASE_PRICE_TARGET_MARGIN, calcMinPurchasePrice } from '../utils';

interface CalculatorSummaryProps {
  data?: CalculatorData;
  launchBudget?: number;
  couponPromoEnabled?: boolean;
}

const LAUNCH_BUDGET_DEFAULT = 15000;

const CalculatorSummary: React.FC<CalculatorSummaryProps> = ({ data, launchBudget = LAUNCH_BUDGET_DEFAULT, couponPromoEnabled = false }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const defaults: CalculatorData = {
    purchasePrice: 0, shippingCostPerKg: 0, unitWeightKg: 0, shippingPrice: 0,
    storageFee: 0.1,
    sellingPrice: 0, referralFee: 0, fbaFee: 0, otherFees: 0,
    ppcCost: 0, conversionRate: 1, ppcSalesPercentage: 70,
    returnRate: 0, resellableReturns: 75,
    monthlySales: 0, batchQuantity: 400,
    couponPromoEnabled: false,
  };

  const d = data ?? defaults;

  const cogs = d.purchasePrice + d.shippingPrice;
  const referralFeeAmount = (d.sellingPrice * d.referralFee) / 100;
  const otherFeesAmount = (d.sellingPrice * d.otherFees) / 100;

  // Return loss per unit: selling_price × return_rate × (1 - resellable_returns)
  const returnLossPerUnit = useMemo(() => {
    const returnRate = d.returnRate / 100;
    const resellableRate = d.resellableReturns / 100;
    return d.sellingPrice * returnRate * (1 - resellableRate);
  }, [d.sellingPrice, d.returnRate, d.resellableReturns]);

  // Per-unit metrics
  // gross_profit = selling_price - COGS - referral - FBA - other fees
  const grossProfitPerUnit = d.sellingPrice - cogs - referralFeeAmount - d.fbaFee - otherFeesAmount;
  // final_profit = gross_profit - return_loss
  const finalProfitPerUnit = grossProfitPerUnit - returnLossPerUnit;
  const marginPerUnit = d.sellingPrice > 0 ? (grossProfitPerUnit / d.sellingPrice) * 100 : 0;
  // Gross ROI (without PPC/storage/coupon)
  const grossRoi = cogs > 0 ? (grossProfitPerUnit / cogs) * 100 : 0;

  // Batch analysis
  const batchQty = d.batchQuantity || 400;
  const batchCogs = batchQty * cogs;
  const batchRevenue = batchQty * d.sellingPrice;

  // Batch-level costs
  const ppcSalesRate = d.ppcSalesPercentage / 100;
  const conversionRate = d.conversionRate > 0 ? d.conversionRate / 100 : 0.01;
  // PPC: (batch_quantity × ppc_sales_percentage) / conversion_rate × CPC
  const batchPpcCost = (batchQty * ppcSalesRate) / conversionRate * d.ppcCost;
  const batchStorageFee = batchQty * d.storageFee;
  // Coupon/promo: (0.35 × qty) × price × 5% + 0.6 × (0.35 × qty)
  const couponPromoCost = couponPromoEnabled
    ? (0.35 * batchQty) * d.sellingPrice * 0.05 + 0.6 * (0.35 * batchQty)
    : 0;

  // Batch profit flow:
  // 1. total_batch_final_profit = final_profit_per_unit × batch_quantity
  const batchFinalProfit = finalProfitPerUnit * batchQty;
  // 2. Subtract PPC, storage, coupon
  const batchProfit = batchFinalProfit - batchPpcCost - batchStorageFee - couponPromoCost;
  const batchMargin = batchRevenue > 0 ? (batchProfit / batchRevenue) * 100 : 0;
  const batchROI = batchCogs > 0 ? (batchProfit / batchCogs) * 100 : 0;

  // With launch costs
  const batchProfitWithLaunch = batchProfit - launchBudget;
  const batchMarginWithLaunch = batchRevenue > 0 ? (batchProfitWithLaunch / batchRevenue) * 100 : 0;
  const batchROIWithLaunch = batchCogs > 0 ? (batchProfitWithLaunch / batchCogs) * 100 : 0;

  // Return losses for batch (for display)
  const returnQty = Math.round(batchQty * (d.returnRate / 100));
  const batchReturnLoss = batchQty * returnLossPerUnit;

  // PPC order metrics
  const orderCost = conversionRate > 0 ? d.ppcCost / conversionRate : 0;
  const ppcOrderProfit = grossProfitPerUnit - orderCost;

  // For cost breakdown display (per-unit advertising for reference)
  const advertisingCostPerUnit = batchQty > 0 ? batchPpcCost / batchQty : 0;

  // Profit per unit with ALL costs (COGS, FBA, referral, PPC, storage, returns) but WITHOUT launch budget
  const couponPromoCostPerUnit = couponPromoEnabled && batchQty > 0 ? couponPromoCost / batchQty : 0;
  const profitPerUnitWithCosts = finalProfitPerUnit - advertisingCostPerUnit - d.storageFee - couponPromoCostPerUnit;
  const marginPerUnitWithCosts = d.sellingPrice > 0 ? (profitPerUnitWithCosts / d.sellingPrice) * 100 : 0;
  const roiPerUnitWithCosts = cogs > 0 ? (profitPerUnitWithCosts / cogs) * 100 : 0;

  // МЗЦ: считается из уже готового profitPerUnitWithCosts, своей прибыли не заводит.
  const minPurchasePrice = calcMinPurchasePrice(profitPerUnitWithCosts, d.purchasePrice, d.sellingPrice);
  const minPurchasePriceMarginPercent = MIN_PURCHASE_PRICE_TARGET_MARGIN * 100;

  const vs = (color: string, fontSize: number, fontWeight?: number) => ({
    content: { color, fontSize, ...(fontWeight ? { fontWeight } : {}) },
  });

  return (
    <Card title={t('idea_calc.calc_summary.title')} variant="outlined" style={{ height: '100%', flex: 1, minWidth: 0 }}>
      <Space orientation="vertical" size={12} style={{ width: '100%' }}>
        {/* Per-unit metrics - gross profit (without PPC/storage) */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.calc_summary.section_gross_per_unit')}</Typography.Text>
        <Row gutter={[8, 8]}>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: grossProfitPerUnit >= 0 ? '#f6ffed' : '#fff2f0', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_profit_per_unit')}
                value={grossProfitPerUnit}
                precision={2}
                prefix="$"
                styles={vs(grossProfitPerUnit >= 0 ? token.colorSuccess : token.colorError, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: marginPerUnit >= 0 ? '#f6ffed' : '#fff2f0', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_margin')}
                value={marginPerUnit}
                precision={2}
                suffix="%"
                styles={vs(marginPerUnit >= 0 ? token.colorSuccess : token.colorError, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: grossRoi >= 0 ? '#f6ffed' : '#fff2f0', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_roi')}
                value={grossRoi}
                precision={2}
                suffix="%"
                styles={vs(grossRoi >= 0 ? token.colorSuccess : token.colorError, 18)}
              />
            </Card>
          </Col>
        </Row>

        {/* Per-unit metrics - with ALL costs (PPC, storage, returns) but WITHOUT launch budget */}
        <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
          {t('idea_calc.calc_summary.section_with_costs')}
        </Typography.Text>
        <Row gutter={[8, 8]}>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: profitPerUnitWithCosts >= 0 ? '#e6f7ff' : '#fff2f0', border: '1px solid #91d5ff', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_profit_per_unit')}
                value={profitPerUnitWithCosts}
                precision={2}
                prefix="$"
                styles={vs(profitPerUnitWithCosts >= 0 ? '#1890ff' : token.colorError, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: marginPerUnitWithCosts >= 0 ? '#e6f7ff' : '#fff2f0', border: '1px solid #91d5ff', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_margin')}
                value={marginPerUnitWithCosts}
                precision={2}
                suffix="%"
                styles={vs(marginPerUnitWithCosts >= 0 ? '#1890ff' : token.colorError, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: roiPerUnitWithCosts >= 0 ? '#e6f7ff' : '#fff2f0', border: '1px solid #91d5ff', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_roi')}
                value={roiPerUnitWithCosts}
                precision={2}
                suffix="%"
                styles={vs(roiPerUnitWithCosts >= 0 ? '#1890ff' : token.colorError, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: minPurchasePrice >= 0 ? '#e6f7ff' : '#fff2f0', border: '1px solid #91d5ff', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_min_purchase_price')}
                value={minPurchasePrice}
                precision={2}
                prefix="$"
                styles={vs(minPurchasePrice >= 0 ? '#1890ff' : token.colorError, 18)}
              />
              <Typography.Text type="secondary" style={{ fontSize: 9 }}>
                {t('idea_calc.calc_summary.stat_min_purchase_price_hint', { margin: minPurchasePriceMarginPercent })}
              </Typography.Text>
            </Card>
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0' }} />

        {/* Batch analysis — without launch costs */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {t('idea_calc.calc_summary.section_batch_no_launch', { qty: batchQty })}
        </Typography.Text>
        <Row gutter={[8, 8]}>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: batchProfit >= 0 ? '#f6ffed' : '#fff2f0', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_profit')}
                value={batchProfit}
                precision={2}
                prefix="$"
                styles={vs(batchProfit >= 0 ? token.colorSuccess : token.colorError, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: batchMargin >= 0 ? '#f6ffed' : '#fff2f0', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_margin')}
                value={batchMargin}
                precision={2}
                suffix="%"
                styles={vs(batchMargin >= 0 ? token.colorSuccess : token.colorError, 18)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px">
            <Card size="small" style={{ background: batchROI >= 0 ? '#f6ffed' : '#fff2f0', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_roi')}
                value={batchROI}
                precision={2}
                suffix="%"
                styles={vs(batchROI >= 0 ? token.colorSuccess : token.colorError, 18)}
              />
            </Card>
          </Col>
        </Row>

        {/* Batch analysis — with launch costs */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {t('idea_calc.calc_summary.section_batch_with_launch', { qty: batchQty, budget: launchBudget.toLocaleString() })}
        </Typography.Text>
        <Row gutter={[8, 8]} style={{ display: 'flex' }}>
          <Col flex="1 0 160px" style={{ display: 'flex' }}>
            <Card
              size="small"
              style={{
                flex: 1,
                textAlign: 'center',
                background: batchProfitWithLaunch >= 0 ? '#f6ffed' : '#fff2f0',
                border: batchProfitWithLaunch >= 0 ? '2px solid #52c41a' : '2px solid #ff4d4f',
              }}
            >
              <Statistic
                title={t('idea_calc.calc_summary.stat_profit')}
                value={batchProfitWithLaunch}
                precision={2}
                prefix="$"
                styles={vs(batchProfitWithLaunch >= 0 ? token.colorSuccess : token.colorError, 18, 600)}
              />
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {batchProfitWithLaunch >= 0
                  ? t('idea_calc.calc_summary.covers_launch')
                  : t('idea_calc.calc_summary.missing_amount', { amount: Math.abs(batchProfitWithLaunch).toLocaleString() })}
              </Typography.Text>
            </Card>
          </Col>
          <Col flex="1 0 160px" style={{ display: 'flex' }}>
            <Card
              size="small"
              style={{
                flex: 1,
                textAlign: 'center',
                background: batchMarginWithLaunch >= 0 ? '#f6ffed' : '#fff2f0',
                border: batchProfitWithLaunch >= 0 ? '2px solid #52c41a' : '2px solid #ff4d4f',
              }}
            >
              <Statistic
                title={t('idea_calc.calc_summary.stat_margin')}
                value={batchMarginWithLaunch}
                precision={2}
                suffix="%"
                styles={vs(batchMarginWithLaunch >= 0 ? token.colorSuccess : token.colorError, 18, 600)}
              />
            </Card>
          </Col>
          <Col flex="1 0 160px" style={{ display: 'flex' }}>
            <Card
              size="small"
              style={{
                flex: 1,
                textAlign: 'center',
                background: batchROIWithLaunch >= 0 ? '#f6ffed' : '#fff2f0',
                border: batchProfitWithLaunch >= 0 ? '2px solid #52c41a' : '2px solid #ff4d4f',
              }}
            >
              <Statistic
                title={t('idea_calc.calc_summary.stat_roi')}
                value={batchROIWithLaunch}
                precision={2}
                suffix="%"
                styles={vs(batchROIWithLaunch >= 0 ? token.colorSuccess : token.colorError, 18, 600)}
              />
            </Card>
          </Col>
        </Row>

        <Descriptions size="small" bordered column={2} style={{ marginTop: 8 }} styles={{ content: { whiteSpace: 'nowrap' } }}>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_batch_cogs')}>${batchCogs.toFixed(0)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_storage_fee')}>${batchStorageFee.toFixed(0)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_batch_ppc')}>${batchPpcCost.toFixed(0)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_launch_budget')}>${launchBudget.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_returns')}>
            <span style={{ color: '#ff4d4f' }}>{t('idea_calc.calc_summary.row_returns_qty_loss', { qty: returnQty, loss: batchReturnLoss.toFixed(0) })}</span>
          </Descriptions.Item>
          {couponPromoEnabled && (
            <Descriptions.Item label={t('idea_calc.calc_summary.row_coupon_promo')}>
              <span style={{ color: '#fa8c16' }}>${couponPromoCost.toFixed(0)}</span>
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* PPC Order Metrics */}
        <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>{t('idea_calc.calc_summary.section_ppc_metrics')}</Typography.Text>
        <Row gutter={[8, 8]}>
          <Col flex="1 0 200px">
            <Card size="small" style={{ background: '#f0f5ff', border: '1px solid #adc6ff', textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_order_cost')}
                value={orderCost}
                precision={2}
                prefix="$"
                styles={vs('#1890ff', 16)}
              />
              <Typography.Text type="secondary" style={{ fontSize: 9 }}>{t('idea_calc.calc_summary.stat_order_cost_formula')}</Typography.Text>
            </Card>
          </Col>
          <Col flex="1 0 200px">
            <Card size="small" style={{ background: ppcOrderProfit >= 0 ? '#f6ffed' : '#fff2f0', border: `1px solid ${ppcOrderProfit >= 0 ? '#b7eb8f' : '#ffa39e'}`, textAlign: 'center' }}>
              <Statistic
                title={t('idea_calc.calc_summary.stat_ppc_order_profit')}
                value={ppcOrderProfit}
                precision={2}
                prefix="$"
                styles={vs(ppcOrderProfit >= 0 ? token.colorSuccess : token.colorError, 16)}
              />
              <Typography.Text type="secondary" style={{ fontSize: 9 }}>{t('idea_calc.calc_summary.stat_ppc_order_profit_formula')}</Typography.Text>
            </Card>
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0' }} />

        {/* Cost breakdown per unit */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.calc_summary.section_cost_breakdown')}</Typography.Text>
        <Descriptions size="small" bordered column={2} styles={{ content: { whiteSpace: 'nowrap' } }}>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_cogs_label')}>${cogs.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_referral')}>${referralFeeAmount.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_fba')}>${d.fbaFee.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_storage')}>${d.storageFee.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_ppc_per_unit')}>${advertisingCostPerUnit.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={t('idea_calc.calc_summary.row_returns_per_unit')}>${returnLossPerUnit.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label={<strong>{t('idea_calc.calc_summary.row_gross_profit')}</strong>} span={2}>
            <strong>${grossProfitPerUnit.toFixed(2)}</strong>
          </Descriptions.Item>
        </Descriptions>
      </Space>
    </Card>
  );
};

export default React.memo(CalculatorSummary);
